/**
 * RicercaRicetteView view component.
 * @module components/View/RicercaRicetteView
 */

import {
  getVocabulary,
  resetContent,
  searchContent,
} from '@plone/volto/actions';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  Dimmer,
  Form,
  Icon,
  Input,
  Loader,
  Pagination,
} from 'semantic-ui-react';
import {
  hasValue,
  seededOrder,
  tokenMatch,
  uniqueOptions,
} from '../../helpers/SearchHelper';
import useSearchFilterState from '../../hooks/useSearchFilterState';
import TitleStandard from '../Commons/TitleStandard';
import CardSearch from './Commons/CardSearch';

const MAX_ITEMS = 6;

const VOCAB_CUCINA = 'baccanale.policy.vocabulary.tipologia_cucina';

const SECTION_AUTORI = 0;
const SECTION_CUCINA = 1;
const SECTION_ANNO = 2;

const PARAM_MAP = {
  autori: { key: 'autori', kind: 'list' },
  cucina: { key: 'cucina', kind: 'list' },
  anno: { key: 'anno', kind: 'list' },
  nome: { key: 'nome', kind: 'text' },
};

const ACCORDION_FOR_FILTER = {
  autori: SECTION_AUTORI,
  cucina: SECTION_CUCINA,
  anno: SECTION_ANNO,
};

// One definition for the option list, the filter and the card subtitle, which
// used to disagree: any ricetta carrying both fields was labelled under one name
// and filtered under the other, so its author's checkbox returned nothing.
const getAutore = (ricetta) => ricetta?.ristorante?.title || ricetta?.autore;

const PREDICATES = {
  autori: (ricetta, values) => tokenMatch(getAutore(ricetta), values),
  cucina: (ricetta, values) => tokenMatch(ricetta?.tipologia_cucina, values),
  anno: (ricetta, values) => tokenMatch(ricetta?.anno, values),
  nome: (ricetta, text) =>
    (ricetta?.title || '').toLowerCase().includes(text.toLowerCase()),
};

export const applyFilters = (items, filter) =>
  (items || []).filter((ricetta) =>
    Object.entries(filter).every(
      ([key, value]) =>
        !hasValue(value) || !PREDICATES[key] || PREDICATES[key](ricetta, value),
    ),
  );

/**
 * RicercaRicetteView view component class.
 * @function RicercaRicetteView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const RicercaRicetteView = ({ content }) => {
  const dispatch = useDispatch();
  const vocabularies = useSelector((state) => state?.vocabularies);
  const ricetteRequest = useSelector(
    (state) => state?.search?.subrequests?.ricette,
  );
  const ricette = ricetteRequest?.items;

  const {
    filter,
    page,
    seed,
    activeIndex,
    setValue,
    setText,
    setPage,
    toggleAccordion,
    isChecked,
    getText,
  } = useSearchFilterState({
    storageKey: 'ricerca_ricette',
    paramMap: PARAM_MAP,
    defaultActiveIndex: [SECTION_AUTORI],
    accordionForFilter: ACCORDION_FOR_FILTER,
  });

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_CUCINA }));
    dispatch(
      searchContent(
        '/',
        { portal_type: ['Ricetta'], fullobjects: 1, b_size: 10000 },
        'ricette',
      ),
    );

    return () => {
      dispatch(resetContent('tipologia_cucina'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, content['@id']]);

  // Derived from the unfiltered result set, so the option lists do not collapse
  // as the user narrows the other filters.
  const autoriOptions = useMemo(() => uniqueOptions(ricette, getAutore), [
    ricette,
  ]);
  const anniOptions = useMemo(
    () => uniqueOptions(ricette, (ricetta) => ricetta?.anno, { sort: 'desc' }),
    [ricette],
  );

  // Order once, on the whole set, before filtering: that way the filtered list is
  // always a slice of one stable permutation, so the same seed and filters bring
  // back the same page of cards.
  const ordered = useMemo(() => seededOrder(ricette, seed), [ricette, seed]);
  const results = useMemo(() => applyFilters(ordered, filter), [
    ordered,
    filter,
  ]);

  const totalPages = Math.max(1, Math.ceil(results.length / MAX_ITEMS));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paged = useMemo(
    () => results.slice((safePage - 1) * MAX_ITEMS, safePage * MAX_ITEMS),
    [results, safePage],
  );

  return (
    <>
      <TitleStandard title={content.title} />
      <div className="menu-view ui container ricerca-menu">
        <div className="mt-5">{content.description}</div>
        <div className="flex mt-5 mb-5 flex-wrap wrapper">
          <div style={{ flex: 1 }}>
            <Accordion fluid styled>
              <Accordion.Title
                active={activeIndex.includes(SECTION_AUTORI)}
                index={SECTION_AUTORI}
                onClick={(e, { index }) => toggleAccordion(index)}
              >
                <Icon name="dropdown" />
                AUTORE
              </Accordion.Title>
              <Accordion.Content active={activeIndex.includes(SECTION_AUTORI)}>
                {autoriOptions.map((i, index) => (
                  <Form.Checkbox
                    key={index}
                    label={i}
                    name="autori"
                    value={i}
                    checked={isChecked('autori', i)}
                    onChange={(e, { checked }) => {
                      setValue('autori', i, checked);
                    }}
                  />
                ))}
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex.includes(SECTION_CUCINA)}
                index={SECTION_CUCINA}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                TIPOLOGIA CUCINA
              </Accordion.Title>
              <Accordion.Content active={activeIndex.includes(SECTION_CUCINA)}>
                <Form>
                  <Form.Group grouped>
                    {vocabularies?.[VOCAB_CUCINA]?.items?.map((i, index) => (
                      <Form.Checkbox
                        key={index}
                        label={i.label}
                        name={VOCAB_CUCINA}
                        value={i.value}
                        checked={isChecked('cucina', i.value)}
                        onChange={(e, { checked }) => {
                          setValue('cucina', i.value, checked);
                        }}
                      />
                    ))}
                  </Form.Group>
                </Form>
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex.includes(SECTION_ANNO)}
                index={SECTION_ANNO}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                ANNO
              </Accordion.Title>
              <Accordion.Content active={activeIndex.includes(SECTION_ANNO)}>
                <Form>
                  <Form.Group grouped>
                    {anniOptions.map((i, index) => (
                      <Form.Checkbox
                        key={index}
                        label={i}
                        name="anno"
                        value={i}
                        checked={isChecked('anno', i)}
                        onChange={(e, { checked }) => {
                          setValue('anno', i, checked);
                        }}
                      />
                    ))}
                  </Form.Group>
                </Form>
              </Accordion.Content>
            </Accordion>
            <div className="mt-2">
              Oppure cerca per nome...
              <Input
                icon="search"
                placeholder="Cerca..."
                className="w-100 mt-05"
                value={getText('nome')}
                onChange={(e, { value }) => {
                  setText('nome', value);
                }}
              />
            </div>
          </div>
          {ricetteRequest && (
            <div className="flex justify-between flex-wrap cards ricette">
              <Dimmer
                active={ricetteRequest?.loading && !ricette?.length}
                inverted
              >
                <Loader
                  active={ricetteRequest?.loading}
                  size={'medium'}
                  inline="centered"
                >
                  Caricamento ...
                </Loader>
              </Dimmer>
              {paged.map((m, index) => (
                <CardSearch
                  key={index}
                  anno={m.anno}
                  titolo={m.title}
                  titolo2={getAutore(m)}
                  linkTo={m['@id']}
                  badgeColor={'gold'}
                />
              ))}
              {totalPages > 1 && (
                <Pagination
                  activePage={safePage}
                  siblingRange={0}
                  firstItem={null}
                  lastItem={null}
                  prevItem={undefined}
                  nextItem={undefined}
                  ellipsisItem="-"
                  totalPages={totalPages}
                  onPageChange={(e, data) => {
                    window.scrollTo(0, 400);
                    setPage(data.activePage);
                  }}
                  className="pagination w-100 flex justify-center"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RicercaRicetteView;
